import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../../components/ui/dialog';
import { Button } from '../../../../components/ui/button';
import { RetentionOfferInfo } from '../../../../services/cancellationServices';

interface RetentionOfferModalProps {
  open: boolean;
  onClose: () => void;
  onAccept: () => void;
  onDecline: () => void;
  retentionOffer: RetentionOfferInfo | null;
  subscriptionType: 'trial' | 'paid';
}

export const RetentionOfferModal: React.FC<RetentionOfferModalProps> = ({
  open,
  onClose,
  onAccept,
  onDecline,
  retentionOffer,
}) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);

  if (!retentionOffer) return null;

  const handleAccept = async () => {
    setIsLoading(true);
    await onAccept();
    setIsLoading(false);
  };

  const isTrialExtension = retentionOffer.type === 'trial_extension';
  const isDiscount = retentionOffer.type === 'discount_50';

  const getTrialDescription = () => {
    const days = retentionOffer.extensionDays || 7;
    return t(
      'settings.billing.cancellation.retentionOffer.trialDescription',
      "We'll extend your trial by {{days}} days so you can explore what you missed.",
      { days },
    );
  };

  const getDiscountDescription = () => {
    if (!retentionOffer.originalPrice || !retentionOffer.discountedPrice) {
      return t(
        'settings.billing.cancellation.retentionOffer.discountDescriptionFallback',
        "We'll give you a special discount.",
      );
    }

    const {
      discountPercentage = 50,
      term = 'annual',
      durationMonths,
    } = retentionOffer;

    // For monthly subscriptions with repeating discount
    if (term === 'monthly' && durationMonths) {
      // eslint-disable-next-line no-template-curly-in-string
      return t(
        'settings.billing.cancellation.retentionOffer.discountDescriptionMonthly',
        // eslint-disable-next-line no-template-curly-in-string
        "We'll give you {{discountPercentage}}% off for {{durationMonths}} months—${{discountedPrice}} instead of ${{originalPrice}} per month.",
        {
          discountPercentage,
          durationMonths,
          discountedPrice: retentionOffer.discountedPrice.toFixed(2),
          originalPrice: retentionOffer.originalPrice.toFixed(2),
        },
      );
    }

    // For annual subscriptions with one-time discount
    // eslint-disable-next-line no-template-curly-in-string
    return t(
      'settings.billing.cancellation.retentionOffer.discountDescriptionAnnual',
      // eslint-disable-next-line no-template-curly-in-string
      "We'll give you {{discountPercentage}}% off your next year—${{discountedPrice}} instead of ${{originalPrice}}.",
      {
        discountPercentage,
        discountedPrice: retentionOffer.discountedPrice.toFixed(2),
        originalPrice: retentionOffer.originalPrice.toFixed(2),
      },
    );
  };

  const getAcceptButtonText = () => {
    if (isLoading)
      return t(
        'settings.billing.cancellation.retentionOffer.processing',
        'Processing...',
      );
    if (isTrialExtension)
      return t(
        'settings.billing.cancellation.retentionOffer.acceptTrial',
        'Extend trial',
      );
    return t(
      'settings.billing.cancellation.retentionOffer.acceptDiscount',
      'Get 50% off',
    );
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {isTrialExtension
              ? t(
                  'settings.billing.cancellation.retentionOffer.trialTitle',
                  'Not ready to decide yet?',
                )
              : t(
                  'settings.billing.cancellation.retentionOffer.discountTitle',
                  'Before you go',
                )}
          </DialogTitle>
          <DialogDescription>
            {isTrialExtension
              ? getTrialDescription()
              : getDiscountDescription()}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Discount Offer - Show pricing visually */}
          {isDiscount &&
            retentionOffer.originalPrice &&
            retentionOffer.discountedPrice && (
              <div className="flex items-baseline gap-2">
                <span className="text-3xl font-bold">
                  ${retentionOffer.discountedPrice.toFixed(2)}
                </span>
                <span className="text-lg text-muted-foreground line-through">
                  ${retentionOffer.originalPrice.toFixed(2)}
                </span>
              </div>
            )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onDecline} disabled={isLoading}>
            {isTrialExtension
              ? t(
                  'settings.billing.cancellation.retentionOffer.declineTrial',
                  'No thanks',
                )
              : t(
                  'settings.billing.cancellation.retentionOffer.declineDiscount',
                  'Proceed with cancellation',
                )}
          </Button>
          <Button onClick={handleAccept} disabled={isLoading}>
            {getAcceptButtonText()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
